import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { NotebookPen, Pin, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ranger/PageHeader";
import { ConfirmButton } from "@/components/ranger/ConfirmButton";
import { BTN_PRIMARY } from "@/pages/Servers";
import { api, errMsg, fmtTime } from "@/lib/api";

const inputCls = "border-[#1E2D4A] bg-[#050811]";

const NoteRow = ({ n, onPin, onDelete }) => (
  <li data-testid={`note-${n.id}`} className={`rounded-sm border px-3 py-2 ${n.pinned ? "border-amber-400/40 bg-amber-400/5" : "border-[#1E2D4A]"}`}>
    <div className="flex items-center gap-2 text-xs">
      <span className="font-mono font-semibold text-slate-100">{n.gamertag}</span>
      <span className="text-slate-500">{n.author} · {fmtTime(n.created_at)}</span>
      <button data-testid={`note-pin-${n.id}`} onClick={() => onPin(n)} title={n.pinned ? "Unpin" : "Pin"} className={`ml-auto transition-colors ${n.pinned ? "text-amber-300" : "text-slate-500 hover:text-slate-200"}`}><Pin className="h-3.5 w-3.5" /></button>
      <ConfirmButton testId={`note-delete-${n.id}`} className="text-slate-500 transition-colors hover:text-red-400" title="Delete this note?" description={`The note on ${n.gamertag} is removed for good.`} onConfirm={() => onDelete(n)}><Trash2 className="h-3.5 w-3.5" /></ConfirmButton>
    </div>
    <p className="mt-1 whitespace-pre-wrap text-sm text-slate-300">{n.text}</p>
  </li>
);

export const AdminNotes = ({ cid }) => {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [f, setF] = useState({ gamertag: "", text: "" });
  const load = useCallback(() => api.get(`/communities/${cid}/notes`, { params: { gamertag: q } }).then((r) => setRows(r.data)).catch(() => {}), [cid, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const add = async (e) => { e.preventDefault(); try { await api.post(`/communities/${cid}/notes`, f); setF({ gamertag: f.gamertag, text: "" }); toast.success("Note saved"); load(); } catch (err) { toast.error(errMsg(err)); } };
  const pin = async (n) => { try { await api.patch(`/communities/${cid}/notes/${n.id}`, { pinned: !n.pinned }); load(); } catch (err) { toast.error(errMsg(err)); } };
  const del = async (n) => { try { await api.delete(`/communities/${cid}/notes/${n.id}`); toast.success("Note deleted"); load(); } catch (err) { toast.error(errMsg(err)); } };
  return (
    <Card title="Admin notes" icon={NotebookPen} testId="notes-card" className="mb-5">
      <p className="mb-3 text-xs text-slate-500">Private staff notes on players. Only moderators and above can see them. Pinned notes stay on top.</p>
      <form onSubmit={add} className="mb-4 grid gap-2 sm:grid-cols-[200px_1fr_auto]">
        <Input data-testid="note-gamertag" required placeholder="Gamertag" value={f.gamertag} onChange={(e) => setF({ ...f, gamertag: e.target.value })} className={`${inputCls} font-mono`} />
        <Textarea data-testid="note-text" required rows={1} placeholder="e.g. Warned for spawn camping, second offence" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} className={inputCls} />
        <button data-testid="note-submit" className={BTN_PRIMARY}>Add note</button>
      </form>
      <Input data-testid="note-search" placeholder="Filter by gamertag" value={q} onChange={(e) => setQ(e.target.value)} className={`mb-3 max-w-xs ${inputCls}`} />
      {rows.length === 0 ? <p data-testid="notes-empty" className="text-sm text-slate-500">No notes yet.</p> : (
        <ul data-testid="notes-list" className="max-h-[28rem] space-y-2 overflow-y-auto">{rows.map((n) => <NoteRow key={n.id} n={n} onPin={pin} onDelete={del} />)}</ul>
      )}
    </Card>
  );
};
